import React, { useEffect, useRef, useState } from "react";
import { Link } from "@inertiajs/react";
import { ArrowLeft, ArrowRight, Image as ImageIcon } from "lucide-react";
import PortfolioImage from "./PortfolioImage";

const SPEED_PX_PER_FRAME = 1.4;
const NUDGE_PX = 320;
const NUDGE_PAUSE_MS = 1500;

const DesignCard = ({ item }) => (
    <Link
        href={item.href}
        className="group flex-none w-28 md:w-48 rounded-xl md:rounded-2xl overflow-hidden bg-white shadow-lg"
    >
        <div className="relative aspect-[4/5] bg-gray-100 overflow-hidden">
            <PortfolioImage
                src={item.thumbnail}
                alt={item.name}
                category={item.categoryLabel}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
        </div>
        <div className="p-2 md:p-3 text-right bg-white">
            <h3 className="font-bold text-gray-900 text-[10px] md:text-sm line-clamp-1">{item.name}</h3>
            {item.categoryLabel && (
                <p className="text-[9px] md:text-xs text-primary-600 font-bold mt-0.5 line-clamp-1">{item.categoryLabel}</p>
            )}
        </div>
    </Link>
);

// One independently auto-scrolling row for a single real category — same
// continuous native-scroll technique as the rest of the page (requestAnimationFrame
// driving real scrollLeft), each row runs its own loop so multiple rows can
// drift independently without fighting each other.
const ShowcaseRow = ({ label, items }) => {
    const scrollRef = useRef(null);
    const [paused, setPaused] = useState(false);
    const loop = items.length > 1 ? [...items, ...items] : items;

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || items.length < 2) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

        // Track position as a float: browsers round scrollLeft, so adding a
        // sub-pixel step straight to it can stall on high-DPI screens.
        let raf;
        let pos = el.scrollLeft;
        const step = () => {
            if (!paused) {
                pos += SPEED_PX_PER_FRAME;
                if (pos >= el.scrollWidth / 2) pos -= el.scrollWidth / 2;
                el.scrollLeft = pos;
            } else {
                pos = el.scrollLeft;
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [paused, items.length]);

    const nudge = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        setPaused(true);
        el.scrollBy({ left: dir * NUDGE_PX, behavior: "smooth" });
        window.clearTimeout(nudge._t);
        nudge._t = window.setTimeout(() => setPaused(false), NUDGE_PAUSE_MS);
    };

    if (!items.length) return null;

    return (
        <div
            className="mb-8 last:mb-0"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="flex justify-end mb-3">
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-[11px] md:text-xs font-bold px-3.5 py-1.5 rounded-full">
                    <ImageIcon className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    {label}
                </span>
            </div>

            <div className="relative">
                {items.length > 1 && (
                    <>
                        <button
                            type="button"
                            onClick={() => nudge(-1)}
                            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 border border-white/25 items-center justify-center text-white transition-colors"
                            aria-label="السابق"
                        >
                            <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => nudge(1)}
                            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 border border-white/25 items-center justify-center text-white transition-colors"
                            aria-label="التالي"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                    </>
                )}

                <div
                    ref={scrollRef}
                    dir="ltr"
                    className="flex gap-2.5 md:gap-4 overflow-x-auto scrollbar-hide"
                    style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    onTouchStart={() => setPaused(true)}
                    onTouchEnd={() => setPaused(false)}
                >
                    {loop.map((item, idx) => (
                        <DesignCard key={`${item.id}-${idx}`} item={item} />
                    ))}
                </div>
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────── */

// Premium dark showcase, split into real category rows (social designs /
// brand identity / website & store builds) instead of one mixed row —
// each group is whatever the backend already sends for that category, so
// it scales automatically as more real work is added. No fabricated
// grouping: rows come straight from the same data pools the rest of this
// page already uses (banners+images, logosOnly, projectsOnly).
const DesignsShowcaseCarousel = ({ groups = [], onViewAll }) => {
    const validGroups = groups.filter((g) => g.items && g.items.length > 0);
    const totalCount = validGroups.reduce((sum, g) => sum + (g.total ?? g.items.length), 0);

    if (validGroups.length === 0) return null;

    return (
        <div
            className="relative rounded-3xl overflow-hidden px-4 py-10 md:px-10 md:py-14 shadow-xl"
            style={{ background: "linear-gradient(160deg, #0B2F82 0%, #1F4FD8 100%)" }}
        >
            <div
                className="pointer-events-none absolute inset-0"
                style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(255,255,255,0.12), transparent)" }}
            />

            <div className="relative text-center mb-8 md:mb-10">
                <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">
                    <ImageIcon className="w-3.5 h-3.5" />
                    تصاميم احترافية
                </span>
                <h2 className="text-2xl md:text-4xl font-black text-white mb-3">
                    أكثر من {totalCount}+ تصميم بوست
                </h2>
                <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto">
                    مجموعة مختارة من تصاميمنا الإبداعية التي نفذناها لعملاء حقيقيين في مختلف المجالات.
                </p>
            </div>

            <div className="relative">
                {validGroups.map((group) => (
                    <ShowcaseRow key={group.key} label={group.label} items={group.items} />
                ))}
            </div>
            <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>

            <div className="relative text-center mt-2">
                <button
                    type="button"
                    onClick={onViewAll}
                    className="inline-flex items-center gap-2 bg-white text-primary-700 font-bold text-sm px-6 py-3 rounded-full hover:bg-white/90 transition-colors"
                >
                    عرض جميع التصاميم
                    <ArrowLeft className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

export default DesignsShowcaseCarousel;
